import { toUsFormat } from 'utils/utils';

/**
 * Shared compact number/currency formatters for analytics chart axes and
 * compact stat labels. Co-located with `chartColors.ts` because the chart
 * primitives and analytics sections both already import from this directory.
 *
 * Thresholds (shared via `compactSuffix`, so both variants stay consistent):
 *   < 1 000      → full value
 *   ≥ 1 000      → k-suffix, 1 dp  (e.g. "1.2k" / "$1.2k")
 *   ≥ 1 000 000  → M-suffix, 1 dp  (e.g. "3.4M" / "$3.4M")
 *
 * Use on a continuous Y-axis to stop large counts/amounts overflowing the
 * axis gutter. Pass directly as `yAxisFormatter` — both signatures are
 * `(value: number) => string`, matching the chart primitives' prop.
 */

/** Decimal places on a compacted (k/M) value. */
const COMPACT_PRECISION = 1;

/**
 * Compacted magnitude string for values ≥ 1 000, or `null` below the threshold
 * so each caller supplies its own below-1000 branch. Single source of the k/M
 * cascade so the two formatters can never drift apart.
 */
const compactSuffix = (n: number): string | null => {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(COMPACT_PRECISION)}M`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(COMPACT_PRECISION)}k`;
	return null;
};

/** Plain count: no currency symbol. e.g. 1234 → "1.2k". */
export function formatCompactNumber(value: number): string {
	return compactSuffix(value) ?? `${value}`;
}

/**
 * Currency: `$`-prefixed, full precision below $1 000 (via `toUsFormat`).
 * Byte-identical to the former `inventoryUtils.formatCompactRevenue` so the
 * fill-ring center labels that consume it are unchanged.
 */
export function formatCompactCurrency(amount: number): string {
	const suffix = compactSuffix(amount);
	return suffix ? `$${suffix}` : toUsFormat(amount);
}
